/**
 * Entrada de archivos de una nota de aseo.
 *
 *  - CELULAR / TABLET (puntero táctil): tres botones grandes (>= 48 px) —
 *    «Tomar foto» (cámara trasera), «Grabar video» (cámara en modo video) y
 *    «Elegir de la galería o archivos» (selección múltiple). Sin zona de
 *    arrastre ni leyenda de Ctrl+V: no sirven con el dedo.
 *  - ESCRITORIO: `<DragDropUpload>` (arrastrar + click) y Ctrl+V, como exige la
 *    regla del proyecto (CLAUDE.md §7, «UX de uploads»). El pegado lo monta el
 *    componente padre con `useClipboardPaste`.
 *
 * Valida peso (máx. 3 GB) y, en los inputs nativos, la duración del video
 * (máx. 20 min); la zona de arrastre ya valida la duración por dentro.
 */

import { useRef } from "react";
import { Button } from "antd";
import { CameraOutlined, FolderOpenOutlined, VideoCameraAddOutlined } from "@ant-design/icons";
import { DragDropUpload } from "../../../Components/DragDropUpload";
import { getMsg } from "../../../utils/antdStatic";
import { formatBytes } from "../../../utils/colaSubida";
import { MAX_VIDEO_SECONDS, mensajeVideoDemasiadoLargo } from "../../../utils/videoDuration";
import { MAX_SUBIDA_BYTES } from "../../../utils/subidaReanudable";
import "../../../Css/notas-movil.css";

/** Texto fijo con los límites reales (se muestra en ambas variantes). */
export const TEXTO_LIMITES = `Videos de hasta ${MAX_VIDEO_SECONDS / 60} min. Máximo ${formatBytes(
  MAX_SUBIDA_BYTES,
).replace(",00", "")} por archivo.`;

/** Mensaje de error si el archivo no se puede subir por su peso; null si está bien. */
export function validarPesoArchivo(file: File): string | null {
  if (!(file.size > 0)) return `El archivo "${file.name}" está vacío.`;
  if (file.size > MAX_SUBIDA_BYTES) {
    return `"${file.name}" pesa ${formatBytes(file.size)} y el máximo es ${formatBytes(
      MAX_SUBIDA_BYTES,
    ).replace(",00", "")}.`;
  }
  return null;
}

/** Peso + duración (solo videos). Para los archivos que llegan por inputs nativos. */
export async function validarArchivoNota(file: File): Promise<string | null> {
  return validarPesoArchivo(file) ?? (await mensajeVideoDemasiadoLargo(file));
}

export function NotaEntradaArchivos({
  tactil,
  accept,
  onFiles,
}: {
  tactil: boolean;
  accept: string;
  /** Recibe solo archivos ya validados. */
  onFiles: (files: File[]) => void;
}) {
  const refFoto = useRef<HTMLInputElement>(null);
  const refVideo = useRef<HTMLInputElement>(null);
  const refGaleria = useRef<HTMLInputElement>(null);

  const procesar = async (files: File[]) => {
    const buenos: File[] = [];
    for (const f of files) {
      const error = await validarArchivoNota(f);
      if (error) getMsg().error(error);
      else buenos.push(f);
    }
    if (buenos.length) onFiles(buenos);
  };

  const alElegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Los File sobreviven aunque se limpie el input; se copian antes de limpiarlo.
    const files = Array.from(e.target.files ?? []);
    // Permite volver a elegir el MISMO archivo (p. ej. tras Cancelar).
    e.target.value = "";
    void procesar(files);
  };

  if (!tactil) {
    return (
      <DragDropUpload
        compact
        accept={accept}
        multiple
        onFile={(f) => {
          const error = validarPesoArchivo(f);
          if (error) getMsg().error(error);
          else onFiles([f]);
        }}
        title="Arrastra o haz click para subir fotos, videos o documentos"
        hint={`También puedes pegar con Ctrl+V. ${TEXTO_LIMITES}`}
      />
    );
  }

  return (
    <div className="nm-entrada" data-testid="nm-entrada-movil">
      <Button
        type="primary"
        size="large"
        className="nm-btn-grande"
        icon={<CameraOutlined />}
        data-testid="nm-btn-foto"
        onClick={() => refFoto.current?.click()}
      >
        Tomar foto
      </Button>
      <Button
        type="primary"
        size="large"
        className="nm-btn-grande"
        icon={<VideoCameraAddOutlined />}
        data-testid="nm-btn-video"
        onClick={() => refVideo.current?.click()}
      >
        Grabar video
      </Button>
      <Button
        size="large"
        className="nm-btn-grande"
        icon={<FolderOpenOutlined />}
        data-testid="nm-btn-galeria"
        onClick={() => refGaleria.current?.click()}
      >
        Elegir de la galería o archivos
      </Button>
      <div className="nm-limites">{TEXTO_LIMITES}</div>

      <input
        ref={refFoto}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        data-testid="nm-input-foto"
        onChange={alElegir}
      />
      <input
        ref={refVideo}
        type="file"
        accept="video/*"
        capture="environment"
        style={{ display: "none" }}
        data-testid="nm-input-video"
        onChange={alElegir}
      />
      <input
        ref={refGaleria}
        type="file"
        accept={accept}
        multiple
        style={{ display: "none" }}
        data-testid="nm-input-galeria"
        onChange={alElegir}
      />
    </div>
  );
}
